// useLocationSearch.js
import { useEffect, useState } from 'react';
import { GOOGLEMAPS_API_KEY } from '../../Api/ApiUrls';

// Load Google Maps script once
const loadGoogleMapsScript = (apiKey) => {
  return new Promise((resolve) => {
    if (window.google?.maps?.places) {
      resolve();
      return;
    }
    if (document.querySelector(`script[src*="maps.googleapis.com"]`)) {
      const interval = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    document.head.appendChild(script);
  });
};

export default function useLocationSearch(query) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query || query.length < 3) {
      setResults([]);
      return;
    }

    let cancelled = false;

    const search = async () => {
      setLoading(true);
      try {
        await loadGoogleMapsScript(GOOGLEMAPS_API_KEY);

        if (cancelled) return;

        const autocompleteService = new window.google.maps.places.AutocompleteService();
        const placesService = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );

        // Helper to fetch predictions for a given input string
        const fetchPredictions = (input) =>
          new Promise((resolve) => {
            autocompleteService.getPlacePredictions(
              {
                input,
                componentRestrictions: { country: 'in' },
                types: ['geocode', 'establishment'],
              },
              (predictions, status) => {
                if (
                  status === window.google.maps.places.PlacesServiceStatus.OK &&
                  predictions?.length
                ) {
                  resolve(predictions);
                } else {
                  resolve([]);
                }
              }
            );
          });

        // Fire 3 variations in parallel to collect more predictions
        const [r1, r2, r3] = await Promise.all([
          fetchPredictions(query),
          fetchPredictions(query + ' area'),
          fetchPredictions(query + ' location'),
        ]);

        if (cancelled) return;

        // Merge and deduplicate by place_id
        const seen = new Set();
        const allPredictions = [...r1, ...r2, ...r3].filter((p) => {
          if (seen.has(p.place_id)) return false;
          seen.add(p.place_id);
          return true;
        });

        if (!allPredictions.length) {
          setResults([]);
          setLoading(false);
          return;
        }

        const detailed = await Promise.all(
          allPredictions.slice(0, 15).map(
            (prediction) =>
              new Promise((resolve) => {
                placesService.getDetails(
                  { placeId: prediction.place_id },
                  (place, s) => {
                    if (
                      s === window.google.maps.places.PlacesServiceStatus.OK &&
                      place
                    ) {
                      resolve({
                        label: prediction.description,
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng(),
                      });
                    } else {
                      resolve(null);
                    }
                  }
                );
              })
          )
        );

        if (!cancelled) {
          setResults(detailed.filter(Boolean));
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Location search error:', err);
          setResults([]);
          setLoading(false);
        }
      }
    };

    const timer = setTimeout(search, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  return { results, loading };
}